import React, { useMemo } from "react";
import { Card, Row, Col, Tag, Typography, Skeleton } from "antd";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip as RTooltip, ResponsiveContainer, Legend,
    LineChart, Line,
} from "recharts";
import { ArrowUpOutlined, ArrowDownOutlined, MinusOutlined } from "@ant-design/icons";

// @@ AI-COMMENT: Destructure Text from Typography for comparison summary rows
const { Text, Title } = Typography;

interface TimeBucket {
    bucket: string;
    calls: number;
    status_breakdown: Record<string, number>;
}

interface AgentDay {
    agent: string;
    total_calls: number;
    status_breakdown: Record<string, number>;
    total_duration_seconds: number;
    time_series?: TimeBucket[];
}

interface Props {
    todayReport: AgentDay[];
    yesterdayReport: AgentDay[];
    loadingToday: boolean;
    loadingYesterday: boolean;
}

// @@ AI-COMMENT: Build a lookup map from agent name to its data for quick access
const toMap = (report: AgentDay[]) =>
    Object.fromEntries(report.map(r => [r.agent, r]));

// @@ AI-COMMENT: Compute percentage delta between two values
const delta = (today: number, yesterday: number) => {
    if (yesterday === 0) return today > 0 ? 100 : 0;
    return Math.round(((today - yesterday) / yesterday) * 100);
};

// @@ AI-COMMENT: Render a colored delta badge with up/down/flat arrow
const DeltaBadge = ({ pct }: { pct: number }) => {
    if (pct > 0) return (
        <Tag color="success" style={{ fontSize: 11, margin: 0 }}>
            <ArrowUpOutlined /> +{pct}%
        </Tag>
    );
    if (pct < 0) return (
        <Tag color="error" style={{ fontSize: 11, margin: 0 }}>
            <ArrowDownOutlined /> {pct}%
        </Tag>
    );
    return (
        <Tag style={{ fontSize: 11, margin: 0 }}>
            <MinusOutlined /> 0%
        </Tag>
    );
};

const AgentComparisonSection = ({ todayReport, yesterdayReport, loadingToday, loadingYesterday }: Props) => {
    const isLoading = loadingToday || loadingYesterday;

    // @@ AI-COMMENT: Union of all human agent names from both days, sorted by today's calls descending
    const agentNames = useMemo(() => {
        const names = new Set([
            ...todayReport.filter(r => r.agent !== "Unknown").map(r => r.agent),
            ...yesterdayReport.filter(r => r.agent !== "Unknown").map(r => r.agent),
        ]);
        const todayMap = toMap(todayReport);
        return Array.from(names).sort((a, b) =>
            (todayMap[b]?.total_calls ?? 0) - (todayMap[a]?.total_calls ?? 0)
        );
    }, [todayReport, yesterdayReport]);

    // @@ AI-COMMENT: Build grouped bar chart data with today and yesterday calls per agent
    const chartData = useMemo(() => {
        const todayMap = toMap(todayReport);
        const yestMap = toMap(yesterdayReport);
        return agentNames.map(name => ({
            name: name.split(" ")[0],
            fullName: name,
            Today: todayMap[name]?.total_calls ?? 0,
            Yesterday: yestMap[name]?.total_calls ?? 0,
        }));
    }, [agentNames, todayReport, yesterdayReport]);

    // @@ AI-COMMENT: Aggregate overall totals for the summary KPI strip
    const summary = useMemo(() => {
        const sumCalls = (r: AgentDay[]) =>
            r.filter(x => x.agent !== "Unknown").reduce((s, x) => s + x.total_calls, 0);
        const sumAnswered = (r: AgentDay[]) =>
            r.filter(x => x.agent !== "Unknown").reduce((s, x) => s + (x.status_breakdown?.Answered ?? 0) + (x.status_breakdown?.Unknown ?? 0), 0);
        const sumDur = (r: AgentDay[]) =>
            r.filter(x => x.agent !== "Unknown").reduce((s, x) => s + x.total_duration_seconds, 0);

        const tCalls = sumCalls(todayReport);
        const yCalls = sumCalls(yesterdayReport);
        const tAns = sumAnswered(todayReport);
        const yAns = sumAnswered(yesterdayReport);
        const tDur = sumDur(todayReport);
        const yDur = sumDur(yesterdayReport);

        return {
            calls: { today: tCalls, yesterday: yCalls, pct: delta(tCalls, yCalls) },
            answered: { today: tAns, yesterday: yAns, pct: delta(tAns, yAns) },
            duration: {
                today: `${Math.floor(tDur / 3600)}h ${Math.floor((tDur % 3600) / 60)}m`,
                yesterday: `${Math.floor(yDur / 3600)}h ${Math.floor((yDur % 3600) / 60)}m`,
                pct: delta(tDur, yDur),
            },
        };
    }, [todayReport, yesterdayReport]);

    // @@ AI-COMMENT: Strip date prefix from bucket string to get time-of-day label (e.g. "24 Mar 09:00" -> "09:00")
    const extractTime = (bucket: string): string => {
        // @@ AI-COMMENT: Handle "DD Mon HH:MM" format
        const match = bucket.match(/(\d{2}:\d{2})$/);
        if (match) return match[1];
        // @@ AI-COMMENT: Handle legacy "HH:MM AM" format
        return bucket;
    };

    // @@ AI-COMMENT: Build hourly comparison series with today and yesterday summed across all agents
    const timeSeries = useMemo(() => {
        const sumByTime = (report: AgentDay[]) => {
            const acc: Record<string, number> = {};
            report
                .filter(r => r.agent !== "Unknown")
                .forEach(r => {
                    (r.time_series || []).forEach(tb => {
                        const key = extractTime(tb.bucket);
                        acc[key] = (acc[key] ?? 0) + tb.calls;
                    });
                });
            return acc;
        };
        const todayByTime = sumByTime(todayReport);
        const yestByTime = sumByTime(yesterdayReport);
        // @@ AI-COMMENT: Use yesterday's bucket order as reference since today buckets end at current hour
        const refKeys = Object.keys(yestByTime).length > 0 ? Object.keys(yestByTime) : Object.keys(todayByTime);
        return refKeys
            .filter(k => (todayByTime[k] ?? 0) > 0 || (yestByTime[k] ?? 0) > 0)
            .map(k => ({ time: k, Today: todayByTime[k] ?? 0, Yesterday: yestByTime[k] ?? 0 }));
    }, [todayReport, yesterdayReport]);

    return (
        <div className="space-y-4">
            {/* @@ AI-COMMENT: Section header */}
            <div className="flex items-center gap-2 px-1">
                <div style={{ width: 4, height: 20, background: "#7c3aed", borderRadius: 2 }} />
                <Title level={5} style={{ margin: 0, fontSize: 14 }}>Today vs Yesterday</Title>
                <Text type="secondary" style={{ fontSize: 12 }}>Agent-level comparison</Text>
            </div>

            {/* @@ AI-COMMENT: Overall KPI comparison strip */}
            <Row gutter={[12, 12]}>
                {[
                    { label: "Total Calls", today: summary.calls.today, yesterday: summary.calls.yesterday, pct: summary.calls.pct },
                    { label: "Answered", today: summary.answered.today, yesterday: summary.answered.yesterday, pct: summary.answered.pct },
                    { label: "Handle Time", today: summary.duration.today, yesterday: summary.duration.yesterday, pct: summary.duration.pct },
                ].map(kpi => (
                    <Col xs={24} sm={8} key={kpi.label}>
                        <Card bordered={false} className="shadow-sm rounded-xl" bodyStyle={{ padding: "14px 16px" }}>
                            {isLoading ? <Skeleton active paragraph={{ rows: 1 }} /> : (
                                <>
                                    <Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 4 }}>{kpi.label}</Text>
                                    {/* @@ AI-COMMENT: Today value large, yesterday value small + delta badge */}
                                    <div className="flex items-end gap-2 flex-wrap">
                                        <span style={{ fontSize: 22, fontWeight: 700, color: "#1d1d1d", lineHeight: 1 }}>{kpi.today}</span>
                                        <span style={{ fontSize: 12, color: "#8c8c8c" }}>vs {kpi.yesterday} yest.</span>
                                        <DeltaBadge pct={kpi.pct} />
                                    </div>
                                </>
                            )}
                        </Card>
                    </Col>
                ))}
            </Row>

            {/* @@ AI-COMMENT: Grouped bar chart comparing today vs yesterday per agent */}
            <Card
                bordered={false}
                className="shadow-sm rounded-xl"
                bodyStyle={{ padding: "12px 16px" }}
                title={<span style={{ fontSize: 13 }}>Calls per Agent — Today vs Yesterday</span>}
            >
                {isLoading ? <Skeleton active paragraph={{ rows: 4 }} /> : (
                    <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={chartData} barCategoryGap="30%" barGap={4} margin={{ top: 16, right: 8, left: -10, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} />
                            <RTooltip
                                formatter={(val: any, name: any) => [val, name]}
                                labelFormatter={(l: any, p: any) => p?.[0]?.payload?.fullName || l}
                                contentStyle={{ fontSize: 12, borderRadius: 8 }}
                            />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            {/* @@ AI-COMMENT: Today bars in purple, yesterday bars in light gray */}
                            <Bar dataKey="Today" radius={[4, 4, 0, 0]} fill="#7c3aed" fillOpacity={0.85} />
                            <Bar dataKey="Yesterday" radius={[4, 4, 0, 0]} fill="#d1d5db" fillOpacity={0.9} />
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </Card>

            {/* @@ AI-COMMENT: Hourly line chart overlaying today vs yesterday call volumes by time of day */}
            {timeSeries.length > 0 && (
                <Card
                    bordered={false}
                    className="shadow-sm rounded-xl"
                    bodyStyle={{ padding: "12px 16px" }}
                    title={<span style={{ fontSize: 13 }}>Hourly Pattern — Today vs Yesterday</span>}
                >
                    {isLoading ? <Skeleton active paragraph={{ rows: 4 }} /> : (
                        <ResponsiveContainer width="100%" height={220}>
                            <LineChart data={timeSeries} margin={{ top: 10, right: 16, left: -10, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="time" tick={{ fontSize: 10 }} interval={0} angle={-35} textAnchor="end" height={40} />
                                <YAxis tick={{ fontSize: 11 }} />
                                <RTooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                                <Legend wrapperStyle={{ fontSize: 11 }} />
                                {/* @@ AI-COMMENT: Today line in purple, yesterday line in gray */}
                                <Line type="monotone" dataKey="Today" stroke="#7c3aed" strokeWidth={2.5} dot={false} activeDot={{ r: 5 }} />
                                <Line type="monotone" dataKey="Yesterday" stroke="#9ca3af" strokeWidth={2} strokeDasharray="5 3" dot={false} activeDot={{ r: 4 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    )}
                </Card>
            )}

            {/* @@ AI-COMMENT: Per-agent comparison rows with today, yesterday, and delta */}
            <Card bordered={false} className="shadow-sm rounded-xl" bodyStyle={{ padding: "0 16px" }}>
                {isLoading ? <Skeleton active className="p-4" /> : (
                    agentNames.map((name, idx) => {
                        const todayData = toMap(todayReport)[name];
                        const yestData = toMap(yesterdayReport)[name];
                        const tCalls = todayData?.total_calls ?? 0;
                        const yCalls = yestData?.total_calls ?? 0;
                        const tAns = todayData?.status_breakdown?.Answered ?? 0;
                        const yAns = yestData?.status_breakdown?.Answered ?? 0;
                        const callDelta = delta(tCalls, yCalls);
                        return (
                            <div
                                key={name}
                                style={{
                                    display: "flex", alignItems: "center", gap: 12,
                                    padding: "10px 0",
                                    borderBottom: idx < agentNames.length - 1 ? "1px solid #f5f5f5" : "none",
                                }}
                            >
                                {/* @@ AI-COMMENT: Agent name column */}
                                <Text strong style={{ width: 110, fontSize: 12, flexShrink: 0 }}>{name}</Text>
                                {/* @@ AI-COMMENT: Today calls */}
                                <div style={{ textAlign: "center", minWidth: 60 }}>
                                    <Text style={{ fontSize: 15, fontWeight: 700, color: "#7c3aed" }}>{tCalls}</Text>
                                    <Text type="secondary" style={{ fontSize: 10, display: "block" }}>Today</Text>
                                </div>
                                {/* @@ AI-COMMENT: Yesterday calls */}
                                <div style={{ textAlign: "center", minWidth: 60 }}>
                                    <Text style={{ fontSize: 15, fontWeight: 600, color: "#8c8c8c" }}>{yCalls}</Text>
                                    <Text type="secondary" style={{ fontSize: 10, display: "block" }}>Yesterday</Text>
                                </div>
                                {/* @@ AI-COMMENT: Delta badge for calls */}
                                <DeltaBadge pct={callDelta} />
                                {/* @@ AI-COMMENT: Answered today vs yesterday */}
                                <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
                                    <Text type="secondary" style={{ fontSize: 11 }}>Answered:</Text>
                                    <Tag color="success" style={{ margin: 0, fontSize: 11 }}>{tAns}</Tag>
                                    <Text type="secondary" style={{ fontSize: 11 }}>vs</Text>
                                    <Tag style={{ margin: 0, fontSize: 11, background: "#f5f5f5", border: "none", color: "#595959" }}>{yAns}</Tag>
                                    <DeltaBadge pct={delta(tAns, yAns)} />
                                </div>
                            </div>
                        );
                    })
                )}
            </Card>
        </div>
    );
};

export default AgentComparisonSection;
